import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Grid3x3, Users, FileText, Image as ImageIcon,
  MessageSquare, Sparkles, GraduationCap, Receipt, Radio, LogOut, Home,
  Trophy, QrCode, Import, UserCog, Inbox, DollarSign, ShieldCheck, KeyRound, Menu, X,
  Wallet, SlidersHorizontal, BarChart3, History, Award, CreditCard, Building2, HeartHandshake, ArrowLeftRight,
  BadgeCheck, CalendarClock, ClipboardList, MessageCircle, ShoppingCart, BookOpen, BedDouble, Bell,
} from "lucide-react";

// Every nav entry declares which permission `module` it belongs to.
// `admin`  = main admin only
// `always` = admin + any staff (dashboard, overview)
const NAV_GROUPS = [
  {
    heading: null, // top level, no heading
    items: [
      { to: "/admin",                     end: true, label: "Overview",         icon: LayoutDashboard, module: "always"      },
    ],
  },
  {
    heading: "Business",
    items: [
      { to: "/admin/analytics",                      label: "Analytics",        icon: BarChart3,       module: "accounting"  },
      { to: "/admin/day-close",                      label: "Day Close",        icon: Wallet,          module: "accounting"  },
      { to: "/admin/accounting",                     label: "Accounting",       icon: Wallet,          module: "accounting"  },
      { to: "/admin/kit-sales",                      label: "Kit Sales",        icon: ShoppingCart,    module: "accounting"  },
      { to: "/admin/cash-reconcile",                 label: "Cash Reconcile",   icon: Wallet,          module: "accounting"  },
      { to: "/admin/ledgers",                        label: "Accounting Ledgers", icon: BookOpen,      module: "accounting"  },
      { to: "/admin/suppliers",                      label: "Suppliers",        icon: Users,           module: "accounting"  },
      { to: "/admin/fee-sheets",                     label: "Fee Sheets",       icon: Receipt,         module: "pricing"     },
      { to: "/admin/transactions",                   label: "Online Payments",  icon: ArrowLeftRight,  module: "accounting"  },
      { to: "/admin/donations",                      label: "Donations (80G)",  icon: HeartHandshake,  module: "accounting"  },
    ],
  },
  {
    heading: "Bookings & Pricing",
    items: [
      { to: "/admin/slot-matrix",                    label: "Slot Matrix",      icon: Grid3x3,         module: "bookings"    },
      { to: "/admin/event-billing",                  label: "Event Billing",    icon: FileText,        module: "bookings"    },
      { to: "/admin/service-pricing",                label: "Slot Booking Prices (Hourly)", icon: SlidersHorizontal, module: "pricing"   },
      { to: "/admin/pricing-rules",                  label: "Pricing Rules",    icon: SlidersHorizontal, module: "pricing"   },
      { to: "/admin/membership-plans",               label: "Membership Plans", icon: BadgeCheck,    module: "pricing"     },
      { to: "/admin/members",                        label: "Membership Subscribers", icon: BadgeCheck,   module: "academy"     },
      { to: "/admin/staff-reserve",                  label: "Desk Reservation", icon: ClipboardList,   module: "bookings"    },
      { to: "/admin/desk-checkin",                   label: "Desk Check-in",    icon: QrCode,          module: "bookings"    },
      { to: "/admin/downtimes",                      label: "Scheduled Downtime", icon: CalendarClock, module: "bookings"    },
      { to: "/admin/message-profile",                label: "Message Profile",  icon: MessageCircle,   module: "settings"    },
      { to: "/admin/whatsapp/bridge",                 label: "WA Bridge (QR)",   icon: QrCode,          module: "settings"    },
      { to: "/admin/whatsapp/campaigns",              label: "WA Campaigns",     icon: MessageCircle,   module: "settings"    },
      { to: "/admin/notifications",                    label: "Push Notifications", icon: Bell,          module: "settings"    },
      { to: "/admin/star-assist",                     label: "HTP Assist AI",   icon: Sparkles,        module: "settings"    },
      { to: "/admin/contacts",                        label: "Contacts Directory", icon: Users,          module: "academy"     },
      { to: "/admin/tournaments",                    label: "Tournaments",      icon: Trophy,          module: "tournaments" },
    ],
  },
  {
    heading: "Academy & Gym",
    items: [
      { to: "/admin/parents",                        label: "Facility Members",    icon: Users,           module: "academy"     },
      { to: "/admin/wards",                          label: "Academic Ward List",  icon: GraduationCap,   module: "academy"     },
      { to: "/admin/wards/hostel",                   label: "Hostel Ward List",    icon: BedDouble,       module: "academy"     },
      { to: "/admin/coaches",                        label: "Coaches (Login)",  icon: Award,           module: "academy"     },
      { to: "/admin/attendance",                     label: "Attendance",       icon: Sparkles,        module: "academy"     },
      { to: "/admin/batches",                        label: "Batches & Fees",   icon: GraduationCap,   module: "academy"     },
      { to: "/admin/gym-scanner",                    label: "Gym & Swim Scanner", icon: QrCode,          module: "gym"         },
    ],
  },
  {
    heading: "Website & Media",
    items: [
      { to: "/admin/homepage",                       label: "Homepage & Contact", icon: Home,          module: "content"     },
      { to: "/admin/academy-content",                label: "Academy Page",     icon: GraduationCap,   module: "content"     },
      { to: "/admin/admission",                      label: "Admission Page (Website)",   icon: FileText,        module: "content"     },
      { to: "/admin/gallery",                        label: "Gallery (Photos + Videos)", icon: ImageIcon, module: "content"   },
      { to: "/admin/blogs",                          label: "Blogs",            icon: FileText,        module: "content"     },
      { to: "/admin/testimonials",                   label: "Testimonials",     icon: MessageSquare,   module: "content"     },
      { to: "/admin/news",                           label: "News Ticker",      icon: Radio,           module: "content"     },
      { to: "/admin/team",                           label: "Coaches & Admin",  icon: UserCog,         module: "content"     },
      { to: "/admin/contact-messages",               label: "Inbox",            icon: Inbox,           module: "content"     },
    ],
  },
  {
    heading: "System",
    items: [
      { to: "/admin/payment-config",                 label: "Payment Gateway",  icon: CreditCard,      module: "admin"       },
      { to: "/admin/phonepe-report",                 label: "PhonePe Report",   icon: Wallet,          module: "admin"       },
      { to: "/admin/ngo-profile",                    label: "NGO Profile",      icon: Building2,       module: "admin"       },
      { to: "/admin/sports-ingest",                  label: "Sports Ingest",    icon: Import,          module: "logs"        },
      { to: "/admin/logs",                           label: "Mock Webhooks",    icon: Radio,           module: "logs"        },
      { to: "/admin/audit-log",                      label: "Audit Log",        icon: History,         module: "admin"       },
      { to: "/admin/staff",                          label: "Staff & Roles",    icon: ShieldCheck,     module: "admin"       },
    ],
  },
];

function useVisibleGroups(user) {
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((n) => {
      if (n.module === "always") return true;
      if (n.module === "admin")  return user?.role === "admin";
      if (user?.role === "admin") return true;
      return (user?.permissions || []).includes(n.module);
    }),
  })).filter((g) => g.items.length > 0);
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const groups = useVisibleGroups(user);
  const isStaff = user?.role === "staff";

  const renderLink = (item, onClick) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and").replace(/[()+]/g, "").replace(/-+/g, "-").replace(/-$/,"")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-6 py-2 text-sm border-l-2 transition-all ${
          isActive ? "border-brand-orange bg-white/6 text-white" : "border-transparent text-white/60 hover:text-white hover:bg-white/6"
        }`
      }
    >
      <item.icon size={16} /> {item.label}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-[#0B1120] text-white flex" data-testid="admin-layout">
      <aside className="hidden lg:flex flex-col w-64 border-r border-white/12 bg-[#0B1120] sticky top-0 h-screen">
        <div className="p-6 border-b border-white/12">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets-0z36b82j.emergentagent.net/job_court-booking-hub-22/artifacts/fsvri6nu_WhatsApp%20Image%202026-08-04%20at%2018.37.01.jpeg" alt="HTP Sports Hub" className="h-10 w-10 object-cover rounded-sm" />
            <div>
              <div className="font-display text-lg uppercase font-black leading-none">HTP Sports Hub</div>
              <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">
                ERP · {isStaff ? "Staff" : "Admin"}
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {groups.map((g, gi) => (
            <div key={g.heading || `group-${gi}`} className={gi > 0 ? "mt-4" : ""}>
              {g.heading && (
                <div className="px-6 pb-1 text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
                  {g.heading}
                </div>
              )}
              {g.items.map((item) => renderLink(item))}
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/12 space-y-2">
          <div className="text-xs font-mono uppercase tracking-widest text-white/40">{user?.name}</div>
          {isStaff && (
            <div className="text-[10px] font-mono text-brand-blue">
              {(user?.permissions || []).length} module{(user?.permissions || []).length !== 1 ? "s" : ""} granted
            </div>
          )}
          <button
            onClick={() => navigate("/admin/change-password")}
            data-testid="admin-change-password"
            className="w-full flex items-center gap-2 text-xs text-white/60 hover:text-white"
          >
            <KeyRound size={12} /> Change Password
          </button>
          <button
            onClick={() => navigate("/")}
            data-testid="admin-back-home"
            className="w-full flex items-center gap-2 text-xs text-white/60 hover:text-white"
          >
            <Home size={12} /> Public Site
          </button>
          <button
            onClick={async () => { await logout(); navigate("/"); }}
            data-testid="admin-logout"
            className="w-full flex items-center gap-2 text-xs text-red-400 hover:text-red-300"
          >
            <LogOut size={12} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#0B1120] border-b border-white/12 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <img src="https://customer-assets-0z36b82j.emergentagent.net/job_court-booking-hub-22/artifacts/fsvri6nu_WhatsApp%20Image%202026-08-04%20at%2018.37.01.jpeg" alt="HTP Sports Hub" className="h-7 w-7 object-cover rounded-sm" />
          <span className="font-display uppercase font-black text-sm">
            {isStaff ? "Staff ERP" : "Admin ERP"}
          </span>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="admin-mobile-toggle"
          className="text-white p-1"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      {mobileOpen && (
        <div className="lg:hidden fixed top-14 left-0 right-0 z-40 bg-[#0B1120] border-b border-white/12 max-h-[70vh] overflow-y-auto">
          {groups.map((g, gi) => (
            <div key={g.heading || `mgroup-${gi}`} className={gi > 0 ? "mt-3 border-t border-white/8 pt-2" : ""}>
              {g.heading && (
                <div className="px-6 py-2 text-[10px] font-mono uppercase tracking-[0.25em] text-white/30">
                  {g.heading}
                </div>
              )}
              {g.items.map((item) => renderLink(item, () => setMobileOpen(false)))}
            </div>
          ))}
          <div className="p-4 border-t border-white/12 space-y-2 text-xs">
            <button onClick={() => { setMobileOpen(false); navigate("/admin/change-password"); }} className="flex items-center gap-2 text-white/60">
              <KeyRound size={12} /> Change Password
            </button>
            <button onClick={() => { setMobileOpen(false); navigate("/"); }} data-testid="admin-mobile-back-home" className="flex items-center gap-2 text-white/60">
              <Home size={12} /> Public Site
            </button>
            <button onClick={async () => { setMobileOpen(false); await logout(); navigate("/"); }} className="flex items-center gap-2 text-red-400">
              <LogOut size={12} /> Logout
            </button>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 lg:p-8 p-4 pt-20 lg:pt-8">
        <Outlet />
      </main>
    </div>
  );
}
